import React, { useRef, useEffect } from 'react';
import { useCurrency } from '../../../contexts/CurrencyContext';

const FormatStep = ({ formData, handleOptionSelect, handlePrintTypeChange }) => {
    const { formatPrice } = useCurrency();
    // Set default size and frame if not already set
    useEffect(() => {
        if (!formData.size) {
            handleOptionSelect('size', '21cm x 30cm');
        }

        if (!formData.frame) {
            handleOptionSelect('frame', 'No Frame');
        }

        // Set default print type to digital if not already set
        if (!formData.printType) {
            handlePrintTypeChange('digital');
        }

        // Set default background to black if not already set
        if (!formData.background) {
            handleOptionSelect('background', 'black');
        }
    }, []);

    // Create refs for the dropdown elements
    const sizeDropdownButtonRef = useRef(null);
    const sizeDropdownMenuRef = useRef(null);
    const frameDropdownButtonRef = useRef(null);
    const frameDropdownMenuRef = useRef(null);

    // Effect for handling clicks outside the dropdown
    useEffect(() => {
        function handleClickOutside(event) {
            // Close the size dropdown if it's open and the click is outside
            if (formData.sizeDropdownOpen &&
                sizeDropdownButtonRef.current &&
                sizeDropdownMenuRef.current &&
                !sizeDropdownButtonRef.current.contains(event.target) &&
                !sizeDropdownMenuRef.current.contains(event.target)) {
                handleOptionSelect('sizeDropdownOpen', false);
            }

            // Close the frame dropdown if it's open and the click is outside
            if (formData.frameDropdownOpen &&
                frameDropdownButtonRef.current &&
                frameDropdownMenuRef.current &&
                !frameDropdownButtonRef.current.contains(event.target) &&
                !frameDropdownMenuRef.current.contains(event.target)) {
                handleOptionSelect('frameDropdownOpen', false);
            }
        }

        // Add event listener to document
        document.addEventListener("mousedown", handleClickOutside);

        // Clean up the event listener
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [formData.sizeDropdownOpen, formData.frameDropdownOpen, handleOptionSelect]);

    // Get size options based on print type
    const getSizeOptions = () => {
        if (formData.printType === 'digital') {
            return [
                { size: '21cm x 30cm', price: formatPrice(10.00) },
                { size: '30cm x 40cm', price: formatPrice(15.00) },
                { size: '60cm x 90cm', price: formatPrice(20.00) }
            ];
        } else {
            return [
                { size: '21cm x 30cm', price: formatPrice(50.00) },
                { size: '30cm x 40cm', price: formatPrice(75.00) },
                { size: '60cm x 90cm', price: formatPrice(100.00) }
            ];
        }
    };

    // Get price based on selected size and print type
    const getSizePrice = () => {
        if (formData.printType === 'digital') {
            switch (formData.size) {
                case '21cm x 30cm':
                    return formatPrice(10.00);
                case '30cm x 40cm':
                    return formatPrice(15.00);
                case '60cm x 90cm':
                    return formatPrice(20.00);
                default:
                    return formatPrice(10.00);
            }
        } else {
            switch (formData.size) {
                case '21cm x 30cm':
                    return formatPrice(50.00);
                case '30cm x 40cm':
                    return formatPrice(75.00);
                case '60cm x 90cm':
                    return formatPrice(100.00);
                default:
                    return formatPrice(50.00);
            }
        }
    };

    // Get frame price based on selected frame
    const getFramePrice = () => {
        switch (formData.frame) {
            case 'No Frame':
                return formatPrice(0.00);
            case 'Classic Black':
            case 'Classic White':
                return formatPrice(50.00);
            case 'Silver':
                return formatPrice(60.00);
            default:
                return formatPrice(0.00);
        }
    };

    return (
        <div className="space-y-8">
            <div>
                <h3 className="font-light mb-6 text-[20px]">Background Gradient</h3>
                <p className="mb-8 text-gray-300 text-[14px]">
                    Choose a gradient that best complements your star map. Each gradient creates a unique mood and visual impact.
                </p>

                <div className="grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                    <div className="flex flex-col">
                        <div className="flex flex-col md:flex-row justify-between md:items-end">
                            <div className="flex flex-col">
                                <div className="text-[14px] text-[#A0A0A0]">
                                    Classic Backgrounds
                                </div>
                                <div className="text-[20px] mt-0">
                                    Free
                                </div>
                            </div>
                            <div className="flex gap-3 mt-2 md:mt-0">
                                <button
                                    className={`w-[30px] h-[30px] rounded-full border border-gray-400 ${formData.background === 'gray' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                    onClick={() => handleOptionSelect('background', 'gray')}
                                    style={{ backgroundColor: '#808080' }}
                                >
                                </button>
                                <button
                                    className={`w-[30px] h-[30px] rounded-full border border-gray-400 ${formData.background === 'black' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                    onClick={() => handleOptionSelect('background', 'black')}
                                    style={{ backgroundColor: '#000000' }}
                                >
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col">
                        <div className="flex flex-col md:flex-row justify-between md:items-end">
                            <div className="flex flex-col">
                                <div className="text-[14px] text-[#A0A0A0]">
                                    Basic Harmony
                                </div>
                                <div className="text-[20px] mt-0">
                                    {formatPrice(5.00)}
                                </div>
                            </div>
                            <div className="flex gap-3 mt-2 md:mt-0">
                                <button
                                    className={`w-[30px] h-[30px] rounded-full ${formData.background === 'basic-light' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                    onClick={() => handleOptionSelect('background', 'basic-light')}
                                >
                                    <img src="/images/icon-gradients/gradient-1.svg" className="w-full h-full object-cover" />
                                </button>
                                <button
                                    className={`w-[30px] h-[30px] rounded-full ${formData.background === 'basic-dark' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                    onClick={() => handleOptionSelect('background', 'basic-dark')}
                                >
                                    <img src="/images/icon-gradients/gradient-2.svg" className="w-full h-full object-cover" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col">
                        <div className="flex flex-col md:flex-row justify-between md:items-end">
                            <div className="flex flex-col">
                                <div className="text-[14px] text-[#A0A0A0]">
                                    Sunset Vibe
                                </div>
                                <div className="text-[20px] mt-0">
                                    {formatPrice(8.00)}
                                </div>
                            </div>
                            <div className="flex gap-3 mt-2 md:mt-0">
                                <button
                                    className={`w-[30px] h-[30px] rounded-full ${formData.background === 'pastel-white' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                    onClick={() => handleOptionSelect('background', 'pastel-white')}
                                >
                                    <img src="/images/icon-gradients/gradient-3.svg" className="w-full h-full object-cover" />
                                </button>
                                <button
                                    className={`w-[30px] h-[30px] rounded-full ${formData.background === 'pastel-blue' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                    onClick={() => handleOptionSelect('background', 'pastel-blue')}
                                >
                                    <img src="/images/icon-gradients/gradient-4.svg" className="w-full h-full object-cover" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col">
                        <div className="flex flex-col md:flex-row justify-between md:items-end">
                            <div className="flex flex-col">
                                <div className="text-[14px] text-[#A0A0A0]">
                                    Pastel Whispers
                                </div>
                                <div className="text-[20px] mt-0">
                                    {formatPrice(6.50)}
                                </div>
                            </div>
                            <div className="flex gap-3 mt-2 md:mt-0">
                                <button
                                    className={`w-[30px] h-[30px] rounded-full ${formData.background === 'natural-beige' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                    onClick={() => handleOptionSelect('background', 'natural-beige')}
                                >
                                    <img src="/images/icon-gradients/gradient-8.svg" className="w-full h-full object-cover" />
                                </button>
                                <button
                                    className={`w-[30px] h-[30px] rounded-full ${formData.background === 'sunset-pink' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                    onClick={() => handleOptionSelect('background', 'sunset-pink')}
                                >
                                    <img src="/images/icon-gradients/gradient-9.svg" className="w-full h-full object-cover" />
                                </button>
                                <button
                                    className={`w-[30px] h-[30px] rounded-full ${formData.background === 'sunset-orange' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                    onClick={() => handleOptionSelect('background', 'sunset-orange')}
                                >
                                    <img src="/images/icon-gradients/gradient-10.svg" className="w-full h-full object-cover" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col">
                        <div className="flex flex-col md:flex-row justify-between md:items-end">
                            <div className="flex flex-col">
                                <div className="text-[14px] text-[#A0A0A0]">
                                    Natural Soul
                                </div>
                                <div className="text-[20px] mt-0">
                                    {formatPrice(7.00)}
                                </div>
                            </div>
                            <div className="flex gap-3 mt-2 md:mt-0">
                                <button
                                    className={`w-[30px] h-[30px] rounded-full ${formData.background === 'natural-gold' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                    onClick={() => handleOptionSelect('background', 'natural-gold')}
                                >
                                    <img src="/images/icon-gradients/gradient-6.svg" className="w-full h-full object-cover" />
                                </button>
                                <button
                                    className={`w-[30px] h-[30px] rounded-full ${formData.background === 'natural-brown' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                    onClick={() => handleOptionSelect('background', 'natural-brown')}
                                >
                                    <img src="/images/icon-gradients/gradient-7.svg" className="w-full h-full object-cover" />
                                </button>
                                <button
                                    className={`w-[30px] h-[30px] rounded-full ${formData.background === 'neon-blue' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                    onClick={() => handleOptionSelect('background', 'neon-blue')}
                                >
                                    <img src="/images/icon-gradients/gradient-12.png" className="w-full h-full object-cover" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col">
                        <div className="flex flex-col md:flex-row justify-between md:items-end">
                            <div className="flex flex-col">
                                <div className="text-[14px] text-[#A0A0A0]">
                                    Neon Pop
                                </div>
                                <div className="text-[20px] mt-0">
                                    {formatPrice(9.00)}
                                </div>
                            </div>
                            <div className="flex gap-3 mt-2 md:mt-0">
                                <button
                                    className={`w-[30px] h-[30px] rounded-full ${formData.background === 'pastel-pink' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                    onClick={() => handleOptionSelect('background', 'pastel-pink')}
                                >
                                    <img src="/images/icon-gradients/gradient-5.svg" className="w-full h-full object-cover" />
                                </button>
                                <button
                                    className={`w-[30px] h-[30px] rounded-full ${formData.background === 'neon-purple' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                    onClick={() => handleOptionSelect('background', 'neon-purple')}
                                >
                                    <img src="/images/icon-gradients/gradient-11.png" className="w-full h-full object-cover" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col">
                        <div className="flex flex-col md:flex-row justify-between md:items-end">
                            <div className="flex flex-col">
                                <div className="text-[14px] text-[#A0A0A0]">
                                    Golden Elegance
                                </div>
                                <div className="text-[20px] mt-0">
                                    {formatPrice(11.00)}
                                </div>
                            </div>
                            <div className="flex gap-3 mt-2 md:mt-0">
                                <button
                                    className={`w-[30px] h-[30px] rounded-full ${formData.background === 'golden-dark' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                    onClick={() => handleOptionSelect('background', 'golden-dark')}
                                >
                                    <img src="/images/icon-gradients/gradient-14.png" className="w-full h-full object-cover" />
                                </button>
                                <button
                                    className={`w-[30px] h-[30px] rounded-full ${formData.background === 'golden-black' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                    onClick={() => handleOptionSelect('background', 'golden-black')}
                                >
                                    <img src="/images/icon-gradients/gradient-13.png" className="w-full h-full object-cover" />
                                </button>
                                <button
                                    className={`w-[30px] h-[30px] rounded-full ${formData.background === 'golden-gold' ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                                    onClick={() => handleOptionSelect('background', 'golden-gold')}
                                >
                                    <img src="/images/icon-gradients/gradient-15.png" className="w-full h-full object-cover" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <h3 className="font-light mb-6 text-[20px]">Choose Your Format & Size</h3>
                <div className="flex flex-col md:flex-row gap-4">
                    {[
                        {
                            id: 'digital',
                            label: 'Digital Download (PDF)',
                            description: 'High-resolution file delivered instantly by email — print it anytime, anywhere. Perfect for last-minute gifts!'
                        },
                        {
                            id: 'printed',
                            label: 'Printed Poster',
                            description: `Premium-quality print shipped to your door — ready to frame. ${formatPrice(20)} shipping fee applies.`
                        }
                    ].map((option) => (
                        <div
                            key={option.id}
                            className={`relative cursor-pointer rounded-[30px] p-6 text-center border flex-1 ${formData.printType === option.id ? 'border-white bg-[#181819]' : 'border-[#181819] bg-[#181819]/60'}`}
                            onClick={() => handlePrintTypeChange(option.id)}
                        >
                            <div className="font-light mb-2 text-[20px]">{option.label}</div>
                            <div className="text-[14px] text-gray-[#A0A0A0]">{option.description}</div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-8">
                <div className="flex mb-2">
                    <div className="flex-1 text-[14px] font-normal">Size</div>
                    <div className="w-[100px] text-[14px] font-normal">Price</div>
                </div>
                <div className="flex gap-4">
                    <div className="flex-1 relative">
                        <div
                            className="rounded-lg border border-white py-2 px-4 flex justify-between items-center w-full cursor-pointer relative z-10"
                            onClick={() => handleOptionSelect('sizeDropdownOpen', !formData.sizeDropdownOpen)}
                            ref={sizeDropdownButtonRef}
                        >
                            <div className="text-[14px] font-light">
                                {formData.size || '21cm x 30cm'}
                            </div>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                        </div>
                        {formData.sizeDropdownOpen && (
                            <div
                                className="absolute top-[calc(100%_+_5px)] left-0 right-0 bg-black rounded-lg border border-white shadow-lg w-full min-w-[280px] z-20 overflow-hidden"
                                ref={sizeDropdownMenuRef}
                                style={{ width: 'calc(100% + 80px)' }}
                            >
                                {getSizeOptions().map((option) => (
                                    <div
                                        key={option.size}
                                        className="px-4 py-2.5 hover:bg-[#181819] cursor-pointer text-gray-200 text-[14px] flex justify-between items-center"
                                        onClick={() => {
                                            handleOptionSelect('size', option.size);
                                            handleOptionSelect('sizeDropdownOpen', false);
                                        }}
                                    >
                                        <span className="mr-8 whitespace-nowrap">{option.size}</span>
                                        <span className="text-gray-400 text-[14px] whitespace-nowrap">{option.price}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-start w-[100px]">
                        <div className="text-[14px] font-light">
                            {getSizePrice()}
                        </div>
                    </div>
                </div>
            </div>

            {formData.printType === 'printed' && (
                <>
                    <div className="border-t border-dotted border-[#A0A0A0] !mt-10 mb-8"></div>

                    <div>
                        <h3 className="font-light mb-6 text-[20px]">Frame Style</h3>
                        <p className="mb-8 text-gray-300 text-[14px]">
                            Complete your star map with the perfect frame — from sleek modern to classic styles, designed to match your space and make your moment shine.
                        </p>

                        <div className="flex mb-2">
                            <div className="flex-1 text-[14px] font-normal">Name</div>
                            <div className="w-[100px] text-[14px] font-normal">Price</div>
                        </div>
                        <div className="flex gap-4">
                            <div className="flex-1 relative">
                                <div
                                    className="rounded-lg border border-white py-2 px-4 flex justify-between items-center w-full cursor-pointer relative z-10"
                                    onClick={() => handleOptionSelect('frameDropdownOpen', !formData.frameDropdownOpen)}
                                    ref={frameDropdownButtonRef}
                                >
                                    <div className="flex items-center text-[14px] font-light">
                                        {formData.frame === 'No Frame' && (
                                            <div className="w-[24px] h-[24px] rounded-full border-2 border-dashed border-gray-400 mr-2"></div>
                                        )}
                                        {formData.frame === 'Classic Black' && (
                                            <div className="w-[24px] h-[24px] rounded-full bg-[#222222] mr-2"></div>
                                        )}
                                        {formData.frame === 'Classic White' && (
                                            <div className="w-[24px] h-[24px] rounded-full bg-[#F0F0F0] mr-2"></div>
                                        )}
                                        {formData.frame === 'Silver' && (
                                            <div className="w-[24px] h-[24px] rounded-full bg-[#C0C0C0] mr-2"></div>
                                        )}
                                        {formData.frame}
                                    </div>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="6 9 12 15 18 9"></polyline>
                                    </svg>
                                </div>
                                {formData.frameDropdownOpen && (
                                    <div
                                        className="absolute top-[calc(100%_+_5px)] left-0 right-0 bg-black rounded-lg border border-white shadow-lg w-full min-w-[280px] z-20 overflow-hidden"
                                        ref={frameDropdownMenuRef}
                                        style={{ width: 'calc(100% + 80px)' }}
                                    >
                                        {[
                                            { id: 'No Frame', label: 'No Frame', price: formatPrice(0.00), color: null },
                                            { id: 'Classic Black', label: 'Classic Black', price: formatPrice(50.00), color: '#222222' },
                                            { id: 'Classic White', label: 'Classic White', price: formatPrice(50.00), color: '#F0F0F0' },
                                            { id: 'Silver', label: 'Silver', price: formatPrice(60.00), color: '#C0C0C0' }
                                        ].map((option) => (
                                            <div
                                                key={option.id}
                                                className="px-4 py-2.5 hover:bg-[#181819] cursor-pointer text-gray-200 text-[14px] flex justify-between items-center"
                                                onClick={() => {
                                                    handleOptionSelect('frame', option.id);
                                                    handleOptionSelect('frameDropdownOpen', false);
                                                }}
                                            >
                                                <span className="flex items-center mr-8 whitespace-nowrap">
                                                    {option.color ? (
                                                        <div
                                                            className="w-[22px] h-[22px] rounded-full mr-2"
                                                            style={{ backgroundColor: option.color }}
                                                        ></div>
                                                    ) : (
                                                        <div className="w-[22px] h-[22px] rounded-full border-2 border-dashed border-gray-400 mr-2"></div>
                                                    )}
                                                    {option.label}
                                                </span>
                                                <span className="text-gray-400 text-[14px] whitespace-nowrap">{option.price}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center justify-start w-[100px]">
                                <div className="text-[14px] font-light">
                                    {getFramePrice()}
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-dotted border-[#A0A0A0] !mt-10"></div>
                    </div>
                </>
            )}
        </div>
    );
};

export default FormatStep; 